import * as React from 'react';
import { cn } from '../lib/utils';
import { InfoTooltip } from './info-tooltip';

export type PageHeaderProps = Omit<React.ComponentPropsWithoutRef<'header'>, 'title'> & {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  meta?: React.ReactNode;
  info?: string;
  /** A way back to where this page was opened from (see backLinkClassName). */
  back?: React.ReactNode;
  compact?: boolean;
  children?: React.ReactNode;
  /** Actions as one group; or, ordered rightmost-first: */
  actions?: React.ReactNode;
  /** The page's main action (rightmost). */
  primaryAction?: React.ReactNode;
  secondaryActions?: React.ReactNode;
  tertiaryActions?: React.ReactNode;
};

/** The classes of a page's back link, for a router Link. */
export const backLinkClassName =
  'inline-flex items-center gap-1.5 rounded-sm text-xs font-medium text-muted-foreground transition-colors hover:text-foreground [&_svg]:size-3.5';

/**
 * A page's title, what it is for, and its actions. The title is the page's
 * only h1; the main action sits rightmost.
 */
export function PageHeader({
  title,
  subtitle,
  meta,
  info,
  back,
  compact = false,
  actions,
  primaryAction,
  secondaryActions,
  tertiaryActions,
  className,
  children,
  ...props
}: PageHeaderProps) {
  const tiered = primaryAction || secondaryActions || tertiaryActions;
  const actionArea = tiered ? (
    <>
      {tertiaryActions}
      {secondaryActions}
      {primaryAction}
    </>
  ) : (
    actions
  );

  return (
    <header className={cn(compact ? 'mb-4' : 'mb-6', className)} {...props}>
      {back ? <div className="mb-3">{back}</div> : null}
      {/* The title keeps at least 20rem beside the actions; when both don't
          fit, the actions wrap below it instead of squeezing it. Titles break
          between words, never inside one that fits. */}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 flex-[1_1_20rem]">
          <div className="flex items-center gap-1.5">
            <h1 className="min-w-0 text-page-title text-foreground break-words">{title}</h1>
            {info ? <InfoTooltip text={info} className="mt-1 shrink-0" /> : null}
          </div>
          {subtitle ? (
            <p className="mt-1.5 max-w-[68ch] text-sm text-muted-foreground">{subtitle}</p>
          ) : null}
          {meta ? <div className="mt-2 text-xs text-muted-foreground">{meta}</div> : null}
          {children}
        </div>
        {actionArea ? (
          <div className="flex max-w-full flex-wrap items-center gap-2">{actionArea}</div>
        ) : null}
      </div>
    </header>
  );
}
